import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flag, HeartPulse, Landmark, MapPin, Plane, Train, Trees, Trophy } from "lucide-react";
import { MAPS_EMBED_URL, MAPS_URL } from "@/lib/constants";
import { useLanguage } from "@/lib/i18n";

const EASE = [0.22, 1, 0.36, 1];

const ICONS = {
  train: Train,
  plane: Plane,
  trees: Trees,
  trophy: Trophy,
  flag: Flag,
  landmark: Landmark,
  health: HeartPulse,
};

const TAB_KEYS = ["tutti", "spostamenti", "natura-sport", "sanita"];

export default function Distances() {
  const [tab, setTab] = useState("tutti");
  const { t } = useLanguage();
  const items = t.distances.items.filter((d) => tab === "tutti" || d.tab === tab);

  return (
    <section id="distanze" data-testid="distances-section" className="py-20 lg:py-28 bg-sand/40">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-terracotta font-bold mb-4"
        >
          {t.distances.overline}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-ink max-w-3xl text-balance"
        >
          {t.distances.titleA} <em className="text-terracotta">{t.distances.titleEm}</em>
        </motion.h2>

        <motion.a
          data-testid="distances-maps-link"
          href={MAPS_URL}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
          className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-white border border-[#E2DCD5] px-5 py-3 text-sm font-semibold text-ink transition-all duration-300 hover:border-terracotta hover:text-terracotta hover:-translate-y-0.5 hover:shadow-lg hover:shadow-ink/5"
        >
          <MapPin className="w-4 h-4 text-terracotta" />
          Via Baracca 15, Arcore (MB) · {t.distances.mapsCta}
        </motion.a>

        <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label={t.distances.overline}>
          {TAB_KEYS.map((key) => (
            <button
              key={key}
              data-testid={`distance-tab-${key}`}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`rounded-full px-5 py-2.5 text-xs sm:text-sm font-semibold transition-all duration-300 ${
                tab === key
                  ? "bg-cypress text-linen shadow-lg shadow-cypress/20"
                  : "bg-white border border-[#E2DCD5] text-clay hover:border-terracotta hover:text-terracotta"
              }`}
            >
              {t.distances.tabs[key]}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence mode="popLayout">
            {items.map((item) => {
              const Icon = ICONS[item.icon];
              return (
                <motion.div
                  layout
                  key={item.name}
                  data-testid={`distance-card-${item.tab}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="flex items-center gap-4 rounded-2xl bg-white border border-[#E2DCD5] p-5 hover:border-terracotta/40 hover:shadow-lg hover:shadow-ink/5 transition-all duration-300"
                >
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-sand flex items-center justify-center">
                    <Icon className="w-5 h-5 text-cypress" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm sm:text-base font-semibold text-ink truncate">{item.name}</h3>
                  </div>
                  <span className="shrink-0 rounded-full bg-terracotta/10 text-terracotta-dark font-mono-label text-[10px] sm:text-xs font-bold px-3 py-1.5 whitespace-nowrap">
                    {item.distance}
                  </span>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-12 gap-5 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="lg:col-span-4 rounded-2xl bg-cypress text-linen p-8 flex flex-col justify-between gap-8"
          >
            <div>
              <p className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-sand/70 font-bold mb-3">
                {t.distances.map.overline}
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl font-semibold leading-tight">
                {t.distances.map.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-linen/75">
                {t.distances.map.body}
              </p>
            </div>
            <a
              data-testid="map-directions-btn"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 self-start rounded-full bg-linen text-ink px-6 py-3 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-ink/20"
            >
              <MapPin className="w-4 h-4 text-terracotta" />
              {t.distances.mapsCta}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="lg:col-span-8 rounded-2xl overflow-hidden border border-[#E2DCD5] shadow-sm min-h-[340px]"
          >
            <iframe
              data-testid="map-embed"
              src={MAPS_EMBED_URL}
              title="Mappa di Casa Paola Arcore - Via Baracca 15, Arcore"
              className="w-full h-full min-h-[340px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
